import React from 'react';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import { Chart } from 'react-stockcharts';
import { AreaSeries } from 'react-stockcharts/lib/series';
import { XAxis, YAxis } from 'react-stockcharts/lib/axes';
import { HoverTooltip } from 'react-stockcharts/lib/tooltip';
import { discontinuousTimeScaleProvider } from 'react-stockcharts/lib/scale';
import { MouseCoordinateY, CrossHairCursor } from 'react-stockcharts/lib/coordinates';
import { useParams } from 'react-router-dom';

import { englishLocale } from './stockChart';
import { customNumberFormatter } from '../../../shared/util/utility';
import Logo from '../../../../src/images/logo.png';
import ChartCanvas from './chartCanvas';
import { IChartInformation } from '../../../shared/interface';
import ChartInformationTable from './chartInformationTable';
import ChartTableInfo from './chartInfo';

interface IProps {
	futureTableData: any;
	chartId: string;
	information: IChartInformation;
	language: string;
	chartConfigLength: number;
	chartDetails: any;
	span: string;
}

const ForwardCurve: React.FC<IProps> = (props) => {
	const { futureTableData, chartId, information, language, chartConfigLength, chartDetails, span } = props;
	// eslint-disable-next-line @typescript-eslint/no-unused-vars

	const params = useParams();

	const xScaleProvider = discontinuousTimeScaleProvider.inputDateAccessor((d: any) =>
		d.faelligkeit_f !== '(Cont.)' ? new Date(d.faelligkeit_f) : new Date()
	);
	const { data: chartData, xScale, xAccessor, displayXAccessor } = xScaleProvider(futureTableData.items);

	const tooltipContent =
		() =>
		({ currentItem }: any) => {
			moment.updateLocale('en', englishLocale);
			return {
				x: [`      ${currentItem.faelligkeit_f}`],
				y: [
					{
						label: futureTableData.details?.name_eng,
						value: `${customNumberFormatter(currentItem.hauptkurs, params.decimalSeparator ? params.decimalSeparator : '', 2)}`
					}
				]
			};
		};

	const mouseEdgeAppearance = {
		textFill: '#542605',
		stroke: '#05233B',
		strokeOpacity: 1,
		fill: '#FFF'
	};

	return (
		<>
			{!isEmpty(futureTableData.items) && (
				<div className='chart-container no-padding' id={chartId}>
					<ChartCanvas
						width={1141}
						height={550}
						ratio={2}
						margin={{ left: 20, right: 60, top: 20, bottom: 50 }}
						type='svg'
						seriesName='Stock Prices'
						data={chartData}
						xScale={xScale}
						xAccessor={xAccessor}
						displayXAccessor={displayXAccessor}
						disableMouseMoveEvents={true} // Disable mouse move events
						mouseMoveEvent={true}
						panEvent={true} // Disable panning
						zoomEvent={false}
						clamp={true}
						zoomEnabled={false}
						pointsPerPxThreshold={10}
					>
						<Chart id={0} yExtents={(d: any) => [d.hauptkurs]}>
							<defs>
								<linearGradient id='chartGradiant' x1='150.13' y1='-2.52979' x2='150.13' y2='149.669' gradientUnits='userSpaceOnUse'>
									<stop stopColor='#D5E9FD' />
									<stop offset='0.869054' stopColor='#D5E9FD' stopOpacity='0' />
								</linearGradient>
							</defs>

							<XAxis
								zoomEnabled={false}
								axisAt='bottom'
								orient='bottom'
								tickFormat={(d: number) => {
									return chartData[d].faelligkeit_f;
								}}
								fontWeight={400}
								fontSize={8}
							/>
							<YAxis
								axisAt='right'
								orient='right'
								zoomEnabled={false}
								fontSize={8}
								fontWeight={400}
								tickFormat={(d: any) => customNumberFormatter(d, params.decimalSeparator ? params.decimalSeparator : '')}
							/>
							<CrossHairCursor fontSize={10} strokeDasharray='Solid' />
							<AreaSeries zoomEnabled={false} yAccessor={(d: any) => d.hauptkurs} fill='url(#chartGradiant)' stroke='#14304F' />
							<MouseCoordinateY
								at='right'
								orient='right'
								fontSize={10}
								{...mouseEdgeAppearance}
								displayFormat={(value: number) =>
									customNumberFormatter(value, params.decimalSeparator ? params.decimalSeparator : '', 2)
								}
							/>
							<HoverTooltip tooltipContent={tooltipContent()} fontSize={10} fill='#FFF' bgFill='transparent' />
						</Chart>
						<image href={Logo} x={930} y={450} width='142' height='31' />
					</ChartCanvas>

					<div className='chart-info-table'>
						<ChartInformationTable information={information} language={language || ''} length={chartConfigLength} />
						{chartConfigLength > 1 && <ChartTableInfo chartData={chartData} chartDetails={chartDetails} span={span} />}
					</div>
				</div>
			)}
		</>
	);
};

export default ForwardCurve;
